import { computed, inject, Injectable, signal } from '@angular/core';
import { ConfigService, DevToolsConfig } from '@ps-gui/shared-core';
import { RegisteredDevToolsProvider } from './dev-tools-provider';

/**
 * Registry between the views and the dev tools card. A view registers itself while it is on screen
 * and takes its panel along, so the card shows exactly what the current page can do - and stays
 * hidden when that is nothing. The card knows nothing about what the panels produce.
 */
@Injectable({ providedIn: 'root' })
export class DevToolsCardService {
  private readonly configService = inject(ConfigService<DevToolsConfig>);

  private readonly _providers = signal<RegisteredDevToolsProvider[]>([]);
  private readonly _isCardOpened = signal(false);

  readonly providers = this._providers.asReadonly();
  readonly hasProviders = computed(() => this._providers().length > 0);
  readonly isCardOpened = this._isCardOpened.asReadonly();

  isEnabled(): boolean {
    return this.configService.config?.devToolsEnabled === true;
  }

  register(provider: RegisteredDevToolsProvider): void {
    this._providers.update((providers) => [
      ...providers.filter((registered) => registered.id !== provider.id),
      provider,
    ]);
  }

  unregister(id: string): void {
    this._providers.update((providers) => providers.filter((provider) => provider.id !== id));

    if (!this.hasProviders()) {
      this._isCardOpened.set(false);
    }
  }

  toggleCard(): void {
    this._isCardOpened.update((opened) => !opened);
  }

  closeCard(): void {
    this._isCardOpened.set(false);
  }
}
